import { useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { usePagePermissions } from '../hooks/usePagePermissions';
import {
  useTemplatesList,
  useUpdateTemplate,
  useCreateTemplate,
  usePreviewTemplate,
} from '../hooks/useTemplatesApi';
import { useToast } from '../components/ui/Toast';
import { ApiRequestError } from '../services/api';
import { Button, TEXT_LINK } from '../components/ui/Button';
import { Field, Input, Label, Select, Textarea } from '../components/ui/Field';
import { templateKeyLabel, templateKeyTooltip } from '../lib/labels';
import type {
  MessageTemplate,
  PlaceholderCatalogueEntry,
  TemplateIssue,
  TemplatePreview,
} from '../types/domain';

/**
 * Configuration Masters — message templates.
 *
 * This page exists because migration 07 refused to invent reply wording and said
 * why: "the wording is Regulatory's to author through the masters UI — inventing
 * legal text here would be worse than empty." Until this screen existed, that
 * sentence pointed at nothing, and `Dra.MessageTemplates` held only migration
 * 11's skeletons — all `IsActive=0`, all reading "SKELETON - not approved
 * wording". `compose-draft.ts` selects on `IsActive=1`, so the pilot could not
 * produce a single customer mail.
 *
 * The screen's real job is not text entry. It is to make the placeholder
 * contract visible BEFORE a template goes live, because the failure mode is
 * silent: a template mentioning a placeholder the agent cannot fill produces no
 * draft at all, and the ticket lands in NeedsReview with nothing to look at.
 * That happened for real in P8 (`DRA-2026-00005`, `{{signatoryTitle}}`).
 *
 * So: the server validates with the composer's own parser, this page shows what
 * it said, and the preview renders the finished mail with sample values.
 *
 * The other masters (document types, suppliers, LLM task config) are still
 * planned — this page starts with the one that blocks the live run.
 */

const PILOT_LANGUAGES = ['DE', 'EN', 'FR', 'IT', 'ES'];

/**
 * The market dimension (migration 13). Marcello, 2026-08-26: the Swiss German
 * template must stay separate from the German EU one — 815 dch customers in
 * CHE against 84 in DEU/AUT, all of them German-language.
 *
 * `''` is the market-agnostic row: wording that serves every market with no
 * more specific one, which is what all eight existing rows are. The server
 * validates this list against the markets the composer actually derives, so a
 * market added here without a matching mapping in `compose-draft.ts` is a 400,
 * not a row nothing selects.
 *
 * Labels, not codes: this is a screen where a person chooses an audience.
 */
const MARKET_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All markets' },
  { value: 'CH', label: 'Switzerland' },
];

export function marketLabel(marketCode: string | null): string {
  return MARKET_OPTIONS.find(m => m.value === (marketCode ?? ''))?.label ?? (marketCode as string);
}

/** One row's identity for the "does this already exist" test. */
function comboKey(language: string, marketCode: string | null): string {
  return `${language}|${marketCode ?? ''}`;
}

function Badge({ tone, children }: { tone: 'green' | 'gray' | 'amber' | 'red'; children: React.ReactNode }) {
  const tones = {
    green: 'bg-green-50 text-green-800 border-green-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    red: 'bg-red-50 text-red-800 border-red-200',
  } as const;
  return (
    // P40, task 1: 11 px -> 12 px. Reference §3's floor, and the last of the
    // sub-12 px text in the app - P38 cleared the five board screens, P39 the
    // ticket screen, and /masters is one of the two nobody had swept.
    <span className={`inline-block rounded-ctl border px-1.5 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function IssueList({ issues }: { issues: TemplateIssue[] }) {
  if (issues.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {issues.map((i, idx) => (
        <li
          key={`${i.code}-${i.placeholder ?? idx}`}
          className={`rounded border px-2 py-1.5 text-xs ${
            i.severity === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          <strong>{i.severity === 'error' ? 'Blocks activation' : 'Worth knowing'}:</strong> {i.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * The placeholder reference, resolved for the draft type being edited.
 *
 * `unavailable` entries are not hidden — an author who is looking for
 * `{{documentLink}}` needs to be told it exists and why it will not work here,
 * otherwise they conclude the list is incomplete and type it anyway.
 */
function PlaceholderReference({
  catalogue,
  draftType,
  language,
  onInsert,
}: {
  catalogue: PlaceholderCatalogueEntry[];
  draftType: string;
  /** The language of the template being authored — named in the note below. */
  language: string;
  onInsert: (name: string) => void;
}) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Placeholders the agent can fill — {draftType}
      </div>
      <p className="mt-2 rounded border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs text-blue-900">
        <strong>The names stay English in every language.</strong> They are identifiers, not text — the customer
        never sees them. What they produce is written in this template&rsquo;s language:{' '}
        <span className="font-mono">{'{{salutation}}'}</span> and{' '}
        <span className="font-mono">{'{{requestSummary}}'}</span> come out in <strong>{language}</strong>. So a
        German template is written in German around English codes, and the finished mail is German throughout.
        Press <em>Preview</em> to see it.
      </p>
      <ul className="mt-2 space-y-1.5">
        {catalogue.map(p => {
          const availability = p.availabilityByDraftType[draftType] ?? 'unavailable';
          const usable = availability !== 'unavailable';
          return (
            <li key={p.name} className="text-xs">
              <button
                type="button"
                disabled={!usable}
                onClick={() => onInsert(p.name)}
                className={`font-mono ${
                  usable ? 'text-info hover:underline' : 'cursor-not-allowed text-faint line-through'
                }`}
                title={usable ? 'Insert at the end of the body' : 'Not available for this draft type'}
              >
                {`{{${p.name}}}`}
              </button>{' '}
              {availability === 'always' && <Badge tone="green">always</Badge>}
              {availability === 'conditional' && <Badge tone="amber">sometimes</Badge>}
              {availability === 'unavailable' && <Badge tone="gray">not here</Badge>}
              <div className="text-gray-500">{p.description}</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The finished mail, rendered with sample values — for the editor and for the
 * add-a-language form alike.
 *
 * One implementation on purpose. The panel is the only place an author sees
 * that the English placeholder NAMES produce text in the template's LANGUAGE,
 * and a second copy would eventually show that in one form and not the other —
 * which is the misunderstanding this panel exists to end.
 */
function PreviewPanel({ data, language }: { data: TemplatePreview; language: string }) {
  return (
    <div className="mt-3 rounded border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Preview with sample values — {language}
        </span>
        {data.wouldCompose ? (
          <Badge tone="green">would produce a draft</Badge>
        ) : (
          <Badge tone="red">would be blocked</Badge>
        )}
      </div>
      <div className="mt-2 text-sm font-medium text-gray-900">{data.subject}</div>
      <div
        className="prose prose-sm mt-2 max-w-none border-t border-gray-100 pt-2 text-sm text-gray-800"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.bodyHtml) }}
      />
      {!data.wouldCompose && (
        <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800">
          Unfilled: {data.unfilled.map(u => `{{${u}}}`).join(', ')}. A real ticket using this template would get
          no draft at all and go to Regulatory as NeedsReview.
        </div>
      )}
      <IssueList issues={data.issues} />
    </div>
  );
}

function TemplateEditor({
  template,
  catalogue,
  canEdit,
  onClose,
}: {
  template: MessageTemplate;
  catalogue: PlaceholderCatalogueEntry[];
  canEdit: boolean;
  onClose: () => void;
}) {
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<TemplateIssue[]>([]);

  const update = useUpdateTemplate();
  const preview = usePreviewTemplate();
  const { toast } = useToast();

  const dirty = subject !== template.subject || bodyHtml !== template.bodyHtml;

  async function save(isActive: boolean) {
    setErrors([]);
    try {
      const result = await update.mutateAsync({
        id: template.id,
        subject,
        bodyHtml,
        isActive,
        reason: reason || undefined,
      });
      toast(
        isActive && !template.isActive
          ? `${template.templateKey} (${template.language}) is now ACTIVE — the agent will use it.`
          : `${template.templateKey} (${template.language}) saved.`,
        'success'
      );
      if (result.warnings.length > 0) setErrors(result.warnings);
      else onClose();
    } catch (err) {
      if (err instanceof ApiRequestError && err.error?.details) {
        setErrors(err.error.details.map(d => ({ severity: 'error' as const, code: d.field, message: d.message })));
      } else {
        toast(err instanceof Error ? err.message : 'Save failed', 'error');
      }
    }
  }

  function insert(name: string) {
    setBodyHtml(b => `${b}\n<p>{{${name}}}</p>`);
  }

  return (
    <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/40 p-4">
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div>
          {/* P40: the kit's `Field` owns the id, so the label is wired by construction
              (audit S2-20) and the control carries `FOCUS_RING` (S2-11). The three
              hand-rolled controls here had neither. `font-mono` stays on the two that
              hold template source - a placeholder is easier to spot in it. */}
          <Field label="Subject">
            {(a) => (
              <Input
                {...a}
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={!canEdit}
                className="font-mono"
              />
            )}
          </Field>

          <Field label="Body (HTML)" className="mt-3">
            {(a) => (
              <Textarea
                {...a}
                rows={12}
                value={bodyHtml}
                onChange={e => setBodyHtml(e.target.value)}
                disabled={!canEdit}
                className="font-mono text-xs"
              />
            )}
          </Field>

          <Field
            label="Reason for the change"
            hint="Recorded in the master change log, beside your name and the time."
            className="mt-3"
          >
            {(a) => (
              <Input
                {...a}
                value={reason}
                onChange={e => setReason(e.target.value)}
                disabled={!canEdit}
                placeholder="e.g. wording approved by Regulatory 2026-08-26"
              />
            )}
          </Field>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() =>
                preview.mutate({
                  subject,
                  bodyHtml,
                  draftType: template.draftType,
                  docTypeCode: template.docTypeCode,
                  isActive: template.isActive,
                  language: template.language,
                })
              }
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Preview
            </button>
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => save(template.isActive)}
                  disabled={update.isPending || !dirty}
                  className="rounded bg-gray-800 px-3 py-1.5 text-sm text-white disabled:opacity-40"
                >
                  Save
                </button>
                {!template.isActive ? (
                  <button
                    type="button"
                    onClick={() => save(true)}
                    disabled={update.isPending}
                    className="rounded bg-green-700 px-3 py-1.5 text-sm text-white disabled:opacity-40"
                  >
                    Save and activate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => save(false)}
                    disabled={update.isPending}
                    className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                  >
                    Deactivate
                  </button>
                )}
              </>
            )}
            <button type="button" onClick={onClose} className={`ml-auto text-sm ${TEXT_LINK}`}>
              Close
            </button>
          </div>

          <IssueList issues={errors} />

          {preview.data && <PreviewPanel data={preview.data} language={template.language} />}
        </div>

        <PlaceholderReference
          catalogue={catalogue}
          draftType={template.draftType}
          language={template.language}
          onInsert={insert}
        />
      </div>
    </div>
  );
}

/**
 * Add a template row that does not exist yet — a language, a market, or both.
 *
 * Two dimensions since migration 13, and they are NOT symmetrical:
 *   - a missing MARKET falls back to the all-markets row, so a Swiss variant is
 *     an improvement on wording that already works;
 *   - a missing LANGUAGE falls back to nothing at all. C9 (Roman, 2026-08-25):
 *     the requested language is answered or the ticket goes to Regulatory. Each
 *     language row created here is one language that stops routing to a human.
 *
 * The form says so, because the two look identical in a list and only one of
 * them is a customer waiting.
 */
export function NewTemplateForm({
  templateKey,
  existing,
  seedFrom,
  catalogue,
  onDone,
}: {
  templateKey: string;
  existing: MessageTemplate[];
  seedFrom: MessageTemplate;
  /**
   * The placeholder reference. This form used to be the ONE place a template is
   * authored without it — a bare subject box and a bare textarea — and it is
   * precisely the form somebody opens to add a missing language. Katia,
   * 2026-09-02: "I entered a German text in Masters, but I cannot find the
   * corresponding codes such as {{salutation}}." They were one panel away, in
   * the editor of a row that already existed.
   */
  catalogue: PlaceholderCatalogueEntry[];
  onDone: () => void;
}) {
  const taken = useMemo(
    () => new Set(existing.map(t => comboKey(t.language, t.marketCode))),
    [existing],
  );
  const languagesWithNoRow = useMemo(
    () => PILOT_LANGUAGES.filter(l => !MARKET_OPTIONS.some(m => taken.has(comboKey(l, m.value || null)))),
    [taken],
  );
  /**
   * What the form opens on — a MISSING LANGUAGE first, a missing market only
   * after every language exists.
   *
   * The two dimensions are not equally urgent and the old single loop (language
   * outer, market inner) did not say so: on the real `sds_ack_delivery` key —
   * DE, EN and IT in all markets, no FR, no ES — it stopped at the first free
   * combination, DE/Switzerland, and offered a market variant of a language that
   * already works while two languages had no row at all. A person who opens the
   * form, sees DE preselected and does not pull the select down concludes that
   * DE is all there is; Katia reported exactly that. A missing market costs a
   * Swiss customer a slightly generic mail. A missing language costs them their
   * reply (C9).
   */
  const firstFree = useMemo(() => {
    if (languagesWithNoRow.length > 0) return { language: languagesWithNoRow[0], market: '' };
    for (const l of PILOT_LANGUAGES) {
      for (const m of MARKET_OPTIONS) {
        if (!taken.has(comboKey(l, m.value || null))) return { language: l, market: m.value };
      }
    }
    return null;
  }, [taken, languagesWithNoRow]);

  const [language, setLanguage] = useState(firstFree?.language ?? PILOT_LANGUAGES[0]);
  const [market, setMarket] = useState(firstFree?.market ?? '');
  const [subject, setSubject] = useState(seedFrom.subject);
  const [bodyHtml, setBodyHtml] = useState(seedFrom.bodyHtml);
  const [errors, setErrors] = useState<TemplateIssue[]>([]);
  const create = useCreateTemplate();
  const preview = usePreviewTemplate();
  const { toast } = useToast();

  if (!firstFree) {
    return (
      <div className="mt-2 text-xs text-gray-500">
        Every pilot language exists for this key, in both markets.
      </div>
    );
  }

  const duplicate = taken.has(comboKey(language, market || null));
  const label = market ? `${language}/${marketLabel(market)}` : `${language} (all markets)`;

  async function submit() {
    setErrors([]);
    try {
      await create.mutateAsync({
        templateKey,
        language,
        marketCode: market || null,
        subject,
        bodyHtml,
        isActive: false,
      });
      toast(`${templateKey} — ${label} created, inactive until you activate it.`, 'success');
      onDone();
    } catch (err) {
      if (err instanceof ApiRequestError && err.error?.details) {
        setErrors(err.error.details.map(d => ({ severity: 'error' as const, code: d.field, message: d.message })));
      } else {
        toast(err instanceof Error ? err.message : 'Create failed', 'error');
      }
    }
  }

  function insert(name: string) {
    setBodyHtml(b => `${b}\n<p>{{${name}}}</p>`);
  }

  return (
    <div className="mt-3 rounded border border-gray-200 bg-white p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Add a template for {templateKey}
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Seeded from the {seedFrom.language} text so there is something to translate. It is created inactive —
        the agent uses it only once you activate it.
      </p>
      <p className="mt-1 text-xs text-gray-500">
        <strong>Market</strong> is a refinement: a customer whose market has no row of its own is served by the
        all-markets wording. <strong>Language</strong> is not — a request in a language with no active template
        gets no draft at all and goes to Regulatory.
      </p>
      {languagesWithNoRow.length > 0 && (
        <p className="mt-1 text-xs text-gray-600">
          No template at all yet in <strong>{languagesWithNoRow.join(', ')}</strong> — those are the ones that
          stop a reply. Pull the language select down to reach them.
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Label htmlFor="new-template-language" className="text-xs">Language</Label>
        <Select
          id="new-template-language"
          value={language}
          onChange={e => setLanguage(e.target.value)}
          className="w-auto"
        >
          {PILOT_LANGUAGES.map(l => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </Select>

        <Label htmlFor="new-template-market" className="text-xs">Market</Label>
        <Select
          id="new-template-market"
          value={market}
          onChange={e => setMarket(e.target.value)}
          className="w-auto"
        >
          {MARKET_OPTIONS.map(m => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Select>

        <Button size="sm" onClick={submit} disabled={create.isPending || duplicate}>
          Create
        </Button>
      </div>
      {duplicate && (
        <div className="mt-2 text-xs text-amber-800">
          {label} already exists for {templateKey}. Edit that row instead.
        </div>
      )}

      <div className="mt-3 grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div>
          {/* P40: these two already had `htmlFor` (S2-20 was satisfied here) and no
              focus ring (S2-11 was not). `Field` gives both and generates the id, so
              the hand-written `new-template-*` ids are gone with it. */}
          <Field label="Subject">
            {(a) => (
              <Input
                {...a}
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="font-mono"
              />
            )}
          </Field>

          <Field label="Body (HTML)" className="mt-3">
            {(a) => (
              <Textarea
                {...a}
                rows={12}
                value={bodyHtml}
                onChange={e => setBodyHtml(e.target.value)}
                className="font-mono text-xs"
              />
            )}
          </Field>

          <div className="mt-2">
            <button
              type="button"
              onClick={() =>
                preview.mutate({
                  subject,
                  bodyHtml,
                  draftType: seedFrom.draftType,
                  docTypeCode: seedFrom.docTypeCode,
                  isActive: false,
                  language,
                })
              }
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Preview
            </button>
          </div>

          <IssueList issues={errors} />
          {preview.data && <PreviewPanel data={preview.data} language={language} />}
        </div>

        <PlaceholderReference
          catalogue={catalogue}
          draftType={seedFrom.draftType}
          language={language}
          onInsert={insert}
        />
      </div>
    </div>
  );
}

export default function Masters() {
  const { canEdit } = usePagePermissions('/masters');
  const { data, isLoading, isError } = useTemplatesList();
  const [openId, setOpenId] = useState<number | null>(null);
  const [addingKey, setAddingKey] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, MessageTemplate[]>();
    for (const t of data?.templates ?? []) {
      const list = map.get(t.templateKey) ?? [];
      list.push(t);
      map.set(t.templateKey, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const activeCount = (data?.templates ?? []).filter(t => t.isActive).length;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-ink">Configuration Masters</h1>
      <p className="mt-1 text-sm text-gray-500">
        Business rules as data. Every change is versioned in the MasterChangeLog and audited.
      </p>

      <h2 className="mt-6 text-base font-semibold text-gray-900">Message templates</h2>
      <p className="mt-1 max-w-3xl text-sm text-gray-600">
        The wording the agent fills and a human approves. The agent never writes legal text: it substitutes
        placeholders and asks the model for two short strings only. A template is used only when it is{' '}
        <strong>active</strong>.
      </p>
      <p className="mt-1 max-w-3xl text-sm text-gray-600">
        Each row is one language for one market. <strong>All markets</strong> is the default wording; a market
        row overrides it for that market only, which is how the Swiss German text stays separate from the German
        EU one. A customer whose market has no row of its own falls back to the all-markets wording — but a
        request in a language with no active template falls back to nothing and goes to Regulatory.
      </p>

      {activeCount === 0 && (
        <div className="mt-3 max-w-3xl rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <strong>No template is active.</strong> Until at least <code>sds_ack_delivery</code> is authored and
          activated, every ticket goes to Regulatory instead of getting a draft, and the pilot cannot produce a
          customer mail.
        </div>
      )}

      {isError && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load templates.
        </div>
      )}
      {isLoading && <div className="mt-4 text-sm text-gray-500">Loading…</div>}

      {data && (
        <div className="mt-4 space-y-4">
          {grouped.map(([key, templates]) => (
            <div key={key} className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                {/* P40, audit S2-16. The card said `sds_ack_delivery` and nothing else,
                    on the page Katia works in - and two of the six keys are both a
                    NegativeReply, sent in completely different situations. The WORDS
                    lead now and the KEY stays beside them in monospace: it is what the
                    API takes, what chooseTemplate selects on, and what somebody quotes
                    in a mail about a template. Names, not instead of codes - the id
                    goes small, which is the older house rule. */}
                <h2
                  className="text-sm font-bold text-ink"
                  title={templateKeyTooltip(key)}
                >
                  {templateKeyLabel(key)}
                </h2>
                <code className="rounded bg-head px-1.5 py-0.5 font-mono text-xs text-muted">{key}</code>
                <Badge tone="gray">{templates[0].draftType}</Badge>
                <Badge tone="gray">{templates[0].audience}</Badge>
                {templates[0].audience === 'Supplier' && <Badge tone="gray">Phase 1b — unused today</Badge>}
                <button
                  type="button"
                  onClick={() => setAddingKey(addingKey === key ? null : key)}
                  className={`ml-auto text-xs ${TEXT_LINK}`}
                >
                  {addingKey === key ? 'Cancel' : 'Add language or market'}
                </button>
              </div>

              <ul className="mt-3 divide-y divide-gray-100">
                {templates.map(t => (
                  <li key={t.id} className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="w-8 font-mono text-sm text-gray-700">{t.language}</span>
                      <Badge tone={t.marketCode ? 'amber' : 'gray'}>{marketLabel(t.marketCode)}</Badge>
                      {t.isActive ? <Badge tone="green">active</Badge> : <Badge tone="gray">inactive</Badge>}
                      {t.isSkeleton && <Badge tone="amber">skeleton — not approved wording</Badge>}
                      <span className="truncate text-sm text-gray-600">{t.subject}</span>
                      <button
                        type="button"
                        onClick={() => setOpenId(openId === t.id ? null : t.id)}
                        className={`ml-auto text-xs ${TEXT_LINK}`}
                      >
                        {openId === t.id ? 'Close' : canEdit ? 'Edit' : 'View'}
                      </button>
                    </div>
                    {openId === t.id && (
                      <TemplateEditor
                        template={t}
                        catalogue={data.catalogue}
                        canEdit={canEdit}
                        onClose={() => setOpenId(null)}
                      />
                    )}
                  </li>
                ))}
              </ul>

              {addingKey === key && (
                <NewTemplateForm
                  templateKey={key}
                  existing={templates}
                  seedFrom={templates[0]}
                  catalogue={data.catalogue}
                  onDone={() => setAddingKey(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <h2 className="mt-8 text-base font-semibold text-muted">Document types · Suppliers · LLM task config</h2>
      <p className="mt-1 text-sm text-muted">
        Still edited by migration. Templates came first because they are what blocks the live run.
      </p>
    </div>
  );
}
